import { toaster } from "@/components/ui/toaster";
import { RequestTypes } from "@/constants/enums";
import { AppRoutes } from "@/constants/routes";
import {
  CatalogoServicioItem,
  createSolicitudServicio,
  getSolicitudesServicio,
} from "@/services/service-requests.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { parseTonInputToNumber } from "@/utils/query-string";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { Dispatch, SetStateAction } from "react";
import { FieldValues, UseFormSetValue } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import { FlightRequestsListResponse } from "./flight-request.schema";

interface CreateFlightRequestHandlerProps {
  values: any;
  setLoadingCreate: (loading: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  catalogo: CatalogoServicioItem[];
}

export const createFlightRequestHandler = async ({
  values,
  setLoadingCreate,
  navigate,
  fetchPermissions,
  catalogo,
}: CreateFlightRequestHandlerProps) => {
  const normalizeSingle = (v: any) => (Array.isArray(v) ? v?.[0] : v);

  const cuenta = Number(normalizeSingle(values?.account?.value));
  if (!Number.isFinite(cuenta) || cuenta <= 0) return;

  const usuarioMobileRaw = values?.usuarioMobile?.value;
  const usuarioMobile = usuarioMobileRaw ? Number(usuarioMobileRaw) : undefined;

  const tipoServicioRaw = normalizeSingle(values?.tipoServicio);
  const tipoServicio = Number(tipoServicioRaw);
  if (!Number.isFinite(tipoServicio) || tipoServicio <= 0) return;

  const selectedServicio = catalogo.find((c) => Number(c.id) === tipoServicio);
  const valorUnitario = Number(selectedServicio?.valorUnitario ?? 0);

  const idProcedenciaRaw = String(normalizeSingle(values?.procedencia) ?? "");
  if (!idProcedenciaRaw) return;

  const idProcedencia = idProcedenciaRaw;
  const fechaDeseada = dayjs(values?.fechaDeseada).toISOString();

  const hectareas = parseTonInputToNumber(values?.hectareas) ?? 0;
  if (!Number.isFinite(hectareas) || hectareas <= 0) return;

  const valorTotal = Number.isFinite(valorUnitario)
    ? Number((valorUnitario * hectareas).toFixed(2))
    : 0;

  const observaciones = String(values?.observaciones ?? "").trim().slice(0, 50);

  setLoadingCreate(true);
  try {
    await createSolicitudServicio({
      cuentaOrigen: cuenta,
      ...(usuarioMobile ? { usuarioMobile } : {}),
      tipoServicio,
      idProcedencia,
      fechaDeseada,
      hectareas,
      cantidadMuestras: 0,
      valorTotal,
      observaciones,
      tipoSolicitud: RequestTypes.Vuelo,
    });

    toaster.create({
      type: "success",
      title: "Solicitud creada exitosamente.",
    });

    navigate(AppRoutes.FLIGHT_REQUESTS);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.FLIGHT_REQUESTS,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(error, "No se pudo crear la solicitud."),
      });
    }

    throw error;
  } finally {
    setLoadingCreate(false);
  }
};

interface FetchFlightRequestsHandlerProps {
  filters?: any;
  showLoad?: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setList: Dispatch<SetStateAction<FlightRequestsListResponse | undefined>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchFlightRequestsHandler = async ({
  filters,
  showLoad = true,
  setLoading,
  setList,
  navigate,
  fetchPermissions,
}: FetchFlightRequestsHandlerProps) => {
  try {
    if (showLoad) setLoading(true);

    const response = await getSolicitudesServicio({
      CuentaOrigen: filters?.cuentaOrigen?.value
        ? Number(filters.cuentaOrigen.value)
        : undefined,
      TipoServicio: filters?.tipoServicio?.[0]
        ? Number(filters.tipoServicio[0])
        : undefined,
      TipoSolicitud: RequestTypes.Vuelo,
      Estados: filters?.Estados,
      FechaDesde: filters?.FechaDesde,
      FechaHasta: filters?.FechaHasta,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": filters?.["P.Records"] ?? 10,
    });

    setList(response ?? undefined);
  } catch (error) {
    const errorObj = error as AxiosError;

    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          errorObj,
          "No se pudieron obtener las solicitudes de vuelo.",
        ),
      });
    }

    throw error;
  } finally {
    if (showLoad) setLoading(false);
  }
};

interface SubmitFlightFiltersHandlerProps {
  event: FieldValues;
  setPage: Dispatch<SetStateAction<number>>;
  fetchList: (filters?: any) => Promise<void>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  filtersEmpty: boolean;
}

export const submitFlightFiltersHandler = async ({
  event,
  setPage,
  fetchList,
  setCurrentFilters,
  setFiltersApplied,
  setOpenFilters,
  filtersEmpty,
}: SubmitFlightFiltersHandlerProps) => {
  const estados = Array.isArray(event?.estados)
    ? event.estados.map(Number).filter((v) => !Number.isNaN(v))
    : undefined;

  const tipoServicio = Array.isArray(event?.tipoServicio)
    ? event.tipoServicio
    : undefined;

  const nextFilters = {
    cuentaOrigen: event?.cuentaOrigen || undefined,
    tipoServicio: tipoServicio?.length ? tipoServicio : undefined,
    Estados: estados?.length ? estados : undefined,
    FechaDesde: event?.fechaDesde,
    FechaHasta: event?.fechaHasta,
    "P.Pagina": 1,
  };

  setPage(1);
  setCurrentFilters(nextFilters);
  setFiltersApplied(!filtersEmpty);
  setOpenFilters(false);

  await fetchList(nextFilters);
};

interface ClearFlightFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setPage: Dispatch<SetStateAction<number>>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  fetchList: (filters?: any) => Promise<void>;
}

export const clearFlightFiltersHandler = async ({
  setValue,
  setFiltersApplied,
  setOpenFilters,
  setPage,
  setCurrentFilters,
  fetchList,
}: ClearFlightFiltersHandlerProps) => {
  setValue("cuentaOrigen", undefined);
  setValue("tipoServicio", []);
  setValue("estados", []);
  setValue("fechaDesde", undefined);
  setValue("fechaHasta", undefined);

  setFiltersApplied(false);
  setOpenFilters(false);
  setPage(1);
  setCurrentFilters(undefined);

  await fetchList({ "P.Pagina": 1 });
};

